import React, { useEffect, useMemo, useState } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { User } from '@/types'
import { Modal } from '@/components/ui/Modal'
import { agGridLocaleFr } from '@/config/ag-grid-locale-fr'
import { AGTableWrapper } from '@/components/ag-grid/AGTableWrapper'
import { getUsersColumnDefs } from '@/column-defs/users.column-defs'
import { useAttachedResellersStore } from '@/stores/attached-resellers.store'
import { toastError } from '@/utils/toast'

interface WholesalerResellersDialogProps {
  wholesaler: User
  onClose: () => void
}

export const WholesalerResellersDialog: React.FC<WholesalerResellersDialogProps> = ({
  wholesaler,
  onClose,
}) => {
  const fetchAttachedResellersByWholesaler = useAttachedResellersStore(
    state => state.fetchAttachedResellersByWholesaler
  )
  const [resellers, setResellers] = useState<User[] | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    fetchAttachedResellersByWholesaler(wholesaler._id)
      .then(data => {
        if (isMounted) {
          setResellers(data)
        }
      })
      .catch(error => {
        console.error('Error loading wholesaler resellers:', error)
        toastError(
          error instanceof Error ? error.message : 'Erreur lors du chargement des revendeurs'
        )
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
  }, [fetchAttachedResellersByWholesaler, wholesaler._id])

  const columnDefs: ColDef[] = useMemo(
    () =>
      getUsersColumnDefs({
        handleEdit: () => {},
        handleDeleteClick: () => {},
        currentUserId: null,
        canEdit: false,
        hideRoleColumn: true,
      }),
    []
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  )

  const wholesalerName = `${wholesaler.firstname} ${wholesaler.lastname}`.trim()

  return (
    <Modal
      title={`Revendeurs rattachés — ${wholesalerName}`}
      confirmButtonText="Fermer"
      onCancel={onClose}
      onConfirm={onClose}
      showCancel={false}
      size="lg"
    >
      {isLoading ? (
        <p className="text-[var(--color-text-secondary)]">Chargement...</p>
      ) : (
        <div className="bg-white rounded-lg border border-[var(--color-border)] overflow-hidden">
          <div
            className="ag-theme-alpine"
            style={{ height: '480px', width: '100%', display: 'flex', flexDirection: 'column' }}
          >
            <AGTableWrapper collection="attached-resellers">
              <AgGridReact
                theme="legacy"
                rowData={resellers}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                pagination={true}
                paginationPageSize={10}
                paginationPageSizeSelector={[10, 20, 50]}
                animateRows={true}
                enableCellTextSelection={true}
                ensureDomOrder={true}
                suppressCellFocus={false}
                headerHeight={48}
                rowHeight={48}
                localeText={agGridLocaleFr}
              />
            </AGTableWrapper>
          </div>
        </div>
      )}
    </Modal>
  )
}
